import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { createTracedAsyncThunk } from '../../../tracing/trace';
import { AppDispatch, RootState } from '../Store';

export interface IClientConfig
{
	MapCentricUrl: string;
	PlantingPlanUrl: string;
	AgriclimeUrl: string;
	MapboxAccessToken: string;
	GeometryEditor: string;
	FeatureFlags: { [key: string]: boolean };
	Flag: { [key: string]: boolean };
	VRSMaxScriptMaxPercent: number;
	VRSMaxScriptMinPercent: number;
}

export const getApplicationConfig = createTracedAsyncThunk<IClientConfig, null, { dispatch: AppDispatch, state: RootState }>(
	'config/get',
	async (context, request: {}, thunkAPI) =>
	{
		try
		{
			const response = await fetch(`${process.env.REACT_APP_API_HOST??''}/api/4/config`);
			return await response.json();
		}
		// Likely a NetError thrown from the Api class
		catch (e)
		{
			return thunkAPI.rejectWithValue(e.message);
		}
	}
);

export const initialState: IClientConfig = {
	MapCentricUrl: '',
	PlantingPlanUrl: '',
	AgriclimeUrl: '',
	MapboxAccessToken: '',
	GeometryEditor: '',
	FeatureFlags: {},
	Flag: {},
	VRSMaxScriptMaxPercent: 20,
	VRSMaxScriptMinPercent: -20,
};

export const configSlice = createSlice({
	name: 'config',
	initialState: initialState,
	reducers: {
	},
	extraReducers: (builder) =>
	{
		builder.addCase(getApplicationConfig.fulfilled, (state, { payload }: PayloadAction<IClientConfig>) =>
		{
			return payload;
		});
	}
});

/**
 * Combine our older FeatureFlags and launch-darkly driven Flag object 
 * together so we can just view it as a unified set.
 */
export const featureFlagSelector = (state: RootState) => 
{
	return { 
		// Original flags dictionary
		...state.config.FeatureFlags,
		// Combined with the new flag dictionary
		...state.config.Flag
	};
};

/**
 * A selector to get the feature flag value
 */
export const getFeatureFlag = (currentFeatureFlags: { [key: string]: boolean }, flagToLocate: string): boolean =>
{
	if (currentFeatureFlags)
	{
		// Test both the lower-cased version (may come from launch darkly, takes precedence)
		// and the case-sensitive version (existing AppSettings implementation)
		return currentFeatureFlags[flagToLocate.toLowerCase()] 
			?? currentFeatureFlags[flagToLocate];
	}
	else
	{
		return false;
	}
};

export enum Features
{
	GrowerCropHealthPdfEnabled = 'GrowerCropHealthPdfEnabled',
	EndOfYearSummaryPdfEnabled = 'EndOfYearSummaryPdfEnabled',
	MaxScript = 'MaxScript',
	InlineUserAdmin = 'InlineUserAdmin',
	WeatherPopup = 'WeatherPopup',
	UpdateGrowerMatchStrengthsEnabled = 'UpdateGrowerMatchStrengthsEnabled',
	JdConnectEnabled = 'JdConnectEnabled',
	EnableSendOrderToCropEdge = 'EnableSendOrderToCropEdge',
	AllowOktaLogin = 'AllowOktaLogin',
	UseAcreOneOrders = 'UseAcreOneOrders',
}